/**
 * Notification Service
 * Service quản lý thông báo trong Admin Portal
 */

import axios from 'axios';
import { AuthService } from './authService';

export interface Notification {
  id: number;
  title: string;
  content: string;
  summary?: string;
  type: string;
  priority: number;
  targetPortal: string;
  targetCourseId?: string;
  targetClassId?: string;
  imageUrl?: string;
  attachmentUrl?: string;
  externalLink?: string;
  isPublished: boolean;
  publishedAt?: string;
  expiresAt?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface NotificationStats {
  total: number;
  published: number;
  draft: number;
  forStudents: number;
  forTeachers: number;
  forAll: number;
  urgent: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
  };
}

class NotificationService {
  private getHeaders() {
    return AuthService.getAuthHeader();
  }

  /**
   * [Admin] Lấy danh sách thông báo
   */
  async getAdminNotifications(params?: {
    page?: number;
    limit?: number;
    type?: string;
    targetPortal?: string;
    isPublished?: string;
  }): Promise<ApiResponse<Notification[]>> {
    try {
      const response = await axios.get<ApiResponse<Notification[]>>('/api/v1/notifications/admin/list', {
        headers: this.getHeaders(),
        params
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể tải danh sách thông báo');
    }
  }

  /**
   * [Admin] Lấy thống kê thông báo
   */
  async getStats(): Promise<ApiResponse<NotificationStats>> {
    try {
      const response = await axios.get<ApiResponse<NotificationStats>>('/api/v1/notifications/admin/stats', {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể tải thống kê');
    }
  }

  /**
   * [Admin] Tạo thông báo mới
   */
  async createNotification(data: Partial<Notification>): Promise<ApiResponse<Notification>> {
    try {
      const response = await axios.post<ApiResponse<Notification>>('/api/v1/notifications/admin', data, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể tạo thông báo');
    }
  }

  /**
   * [Admin] Cập nhật thông báo
   */
  async updateNotification(id: number, data: Partial<Notification>): Promise<ApiResponse<any>> {
    try {
      const response = await axios.put<ApiResponse<any>>(`/api/v1/notifications/admin/${id}`, data, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể cập nhật thông báo');
    }
  }

  /**
   * [Admin] Phát hành thông báo
   */
  async publishNotification(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post<ApiResponse<any>>(`/api/v1/notifications/admin/${id}/publish`, {}, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể phát hành thông báo');
    }
  }

  /**
   * [Admin] Hủy phát hành thông báo
   */
  async unpublishNotification(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post<ApiResponse<any>>(`/api/v1/notifications/admin/${id}/unpublish`, {}, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể hủy phát hành');
    }
  }

  /**
   * [Admin] Xóa thông báo
   */
  async deleteNotification(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await axios.delete<ApiResponse<any>>(`/api/v1/notifications/admin/${id}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể xóa thông báo');
    }
  }
}

export default new NotificationService();

